import React from "react";
import dayjs from "dayjs";
import {
    Badge, Box, Flex, Icon, Text, useColorModeValue, Wrap, WrapItem,
} from "@chakra-ui/react";
import { MdWarning } from "react-icons/md";

/**
 * Discrete one-line audit row showing who reviewed the certification and when.
 * Hidden when reviewed_at is not set.
 * @param {Object} props
 * @param {Object} props.certification - Certification with reviewed_at / reviewed_by_admin_id
 * @returns {React.ReactElement|null}
 */
function AuditRow({ certification }) {
    const color = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    if (!certification?.reviewed_at) return null;
    const adminId = certification.reviewed_by_admin_id;
    const when = dayjs(certification.reviewed_at).format("DD/MM/YYYY HH:mm");
    const adminLabel = adminId ? `admin #${adminId}` : "admin";
    return (
        <Text color={color} fontStyle="italic" fontSize="xs">
            Revu par {adminLabel} le {when}
        </Text>
    );
}

/**
 * Renders entity-specific subtitle (e.g. "(bar)") under the name.
 * Returns null for non-entity certifications.
 * @param {Object} props
 * @param {Object} props.certification - Certification object
 * @returns {React.ReactElement|null}
 */
function EntityTypeSubtitle({ certification }) {
    const color = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    if (certification?._type !== "entity" || !certification?.entity_type) return null;
    return <Text color={color} fontStyle="italic" fontSize="xs">({certification.entity_type})</Text>;
}

/**
 * Renders the warning header and tag list for the missing certification fields.
 * @param {Object} props
 * @param {Array<string>} props.fields - List of missing field names
 * @returns {React.ReactElement}
 */
function MissingFieldsChips({ fields }) {
    return (
        <Box>
            <Flex align="center" gap="6px" mb="6px">
                <Icon as={MdWarning} w="14px" h="14px" color="orange.400" />
                <Text color="orange.400" fontSize="sm" fontWeight="600">Champs manquants:</Text>
            </Flex>
            <Wrap spacing="6px">
                {fields.map((field, idx) => (
                    <WrapItem key={idx}>
                        <Badge colorScheme="orange" borderRadius="full" px="8px" textTransform="none">{field}</Badge>
                    </WrapItem>
                ))}
            </Wrap>
        </Box>
    );
}

/**
 * Displays the document type label + a tag list of missing certification fields.
 * @param {Object} props
 * @param {Object} props.certification - Certification data
 * @returns {React.ReactElement|null}
 */
function MissingFieldsBox({ certification }) {
    const bg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const hasMissing = certification.missing_fields?.length > 0;
    const hasDocType = certification.document_type && certification.document_type !== 'unknown';
    if (!hasDocType && !hasMissing) return null;
    return (
        <Box p="12px" bg={bg} borderRadius="12px">
            {hasDocType && (
                <Text color={labelColor} fontSize="sm" mb={hasMissing ? "8px" : "0"}>
                    <strong>Type de document:</strong> {certification.document_type}
                </Text>
            )}
            {hasMissing && <MissingFieldsChips fields={certification.missing_fields} />}
        </Box>
    );
}

export { AuditRow, EntityTypeSubtitle, MissingFieldsBox };
